import { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Button, Typography, Chip } from '@mui/material';
import { Save, DoNotDisturbAlt, Delete } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { UserApiResponse } from '@/services/userService.ts';
import { useApiStore, useAuthStore } from '@core/store';
import { Role } from '@core/types';
import { FormContainer, WcnCheckbox } from '@/components';
import FormTextField from '@/components/form/FormTextField.tsx';
import FormSelect from '@/components/form/FormSelect.tsx';
import FormMultiSelect from '@/components/form/FormMultiSelect.tsx';
import { FormContainerRef } from '@/components/form/FormContainer.tsx';
import { required, validEmail, validateUserName } from '@core/utils/validation.ts';
import { ShopId } from '@/core/store/slices/apiSlice';
import ConfirmationDialogRequireInput, { ConfirmationDialogRef } from '@/components/ConfirmationDialogRequireInput';

interface UserEditorProps {
    item: UserApiResponse;
    onSave: (item: UserApiResponse) => void;
    onDelete: (item: UserApiResponse) => void;
    onCancel: () => void;
}

const UserEditor = ({ item, onSave, onDelete, onCancel }: UserEditorProps) => {
    const { t } = useTranslation();
    const formRef = useRef<FormContainerRef>(null);
    const [formValid, setFormValid] = useState(false);
    const [formData, setFormData] = useState<UserApiResponse>(item);
    const [userSelectedShopIds, setUserSelectedShopIds] = useState<string[]>([]);
    const [userShopIds, setUserShopIds] = useState<ShopId[]>([]);
    const [shopsLoaded, setShopsLoaded] = useState(false);
    const { shopIds, loadShopIds } = useApiStore();
    const [isSuperAdmin, setIsSuperAdmin] = useState(item.role ? item.role === Role.SuperAdmin : false);
    const [isOrgAdmin, setIsOrgAdmin] = useState(item.role ? item.role === Role.OrganisationAdmin : false);
    const { hasRole, user } = useAuthStore();
    const [userSelectedOrganisations, setUserSelectedOrganisations] = useState<string[]>([]);
    const [showOrganisationMultiSelect, setShowOrganisationMultiSelect] = useState(false);
    const [organisations, setOrganisations] = useState<{ name: string; id: string }[]>([]);
    const confirmationDialogRef = useRef<ConfirmationDialogRef>(null);
    const isSuperAdminPage = item.role ? item.role === Role.SuperAdmin : false;
    const existingUserOrganisations = item.organisationIds ?? [];
    const isSuperAdminOrOrganisationAdmin = isSuperAdmin || isOrgAdmin;

    const loadShops = useCallback(async () => {
        if (shopsLoaded) return;

        await loadShopIds();
    }, [shopsLoaded, loadShopIds]);

    useEffect(() => {
        if (shopIds.length > 0) {
            setUserShopIds(shopIds);
            setShopsLoaded(true);
        }
    }, [shopIds]);

    useEffect(() => {
        setFormData({
            ...item,
            accessToAllStores: item.accessToAllStores ?? true
        });

        const shopIds = item.shopIds || [];
        setUserSelectedShopIds(shopIds);
        const orgIds = item.organisationIds || [user?.organisation?.id ?? ''];
        setUserSelectedOrganisations(orgIds);
    }, [item]);

    useEffect(() => {
        if (isSuperAdminOrOrganisationAdmin) {
            handleFieldChange('accessToAllStores', true);
            setUserSelectedShopIds([]);
        }
    }, [isSuperAdminOrOrganisationAdmin]);

    useEffect(() => {
        if (hasRole(Role.OrganisationAdmin) && !isSuperAdminPage) {
            setShowOrganisationMultiSelect(true);
            setOrganisations(
                (user?.organisationClaims ?? []).map((org) => ({
                    name: org.name,
                    id: org.id
                }))
            );
        }
    }, [user]);

    useEffect(() => {
        loadShops();
    }, []);

    const handleFieldChange = (fieldName: keyof UserApiResponse, value: any) => {
        if (fieldName === 'role') {
            if (formData.role === value) return;
            setIsSuperAdmin(value === Role.SuperAdmin);
            setIsOrgAdmin(value === Role.OrganisationAdmin);
            if (hasRole(Role.OrganisationAdmin)) {
                switch (value) {
                    case Role.SuperAdmin:
                    case Role.OrganisationAdmin:
                        setUserSelectedOrganisations([]);
                        handleFieldChange('organisationIds', []);
                        break;
                    case Role.UnitAdmin:
                    case Role.StoreAdmin:
                    case Role.User:
                        setUserSelectedOrganisations([user?.organisation?.id ?? '']);
                        handleFieldChange('organisationIds', [user?.organisation?.id ?? '']);
                        break;
                }
            }
        }

        if (fieldName === 'accessToAllStores' && value === true) {
            setUserSelectedShopIds([]);
            handleFieldChange('shopIds', null);
        }

        if (fieldName === 'shopIds' && value?.length > 0) {
            handleFieldChange('accessToAllStores', false);
        }
        setFormData((prev) => (prev ? { ...prev, [fieldName]: value } : prev));
    };

    const handleMultipleShopChange = (selectedIds: string[]) => {
        setUserSelectedShopIds(selectedIds);
        handleFieldChange('shopIds', selectedIds.length > 0 ? selectedIds : null);
    };

    const handleMultipleOrganisationChange = (selectedIds: string[]) => {
        setUserSelectedOrganisations(selectedIds);
        handleFieldChange('organisationIds', selectedIds.length > 0 ? selectedIds : null);
    };

    const handleAskSave = () => {
        if (
            formData.role === Role.OrganisationAdmin &&
            formData.organisationIds &&
            formData.organisationIds.length > 1 &&
            formData.organisationIds.some((orgId) => !existingUserOrganisations.includes(orgId))
        ) {
            confirmationDialogRef.current?.openDialog();
        } else {
            handleSave();
        }
    };

    const handleSave = () => {
        const isValid = formRef.current?.validate?.() ?? formValid;
        if (!isValid) {
            return;
        }

        if (isSuperAdmin) {
            formData.organisationIds = [];
        } else if (
            formData.role !== Role.OrganisationAdmin ||
            (formData.role === Role.OrganisationAdmin && (formData.organisationIds?.length ?? 0) === 0)
        ) {
            const selectedOrg = user?.organisation?.id;
            console.log('selectedOrg: ', selectedOrg);
            if (!selectedOrg) return; //ToDo: Show error.

            formData.organisationIds = [selectedOrg];
        }

        // Create a payload for the API
        const payload: UserApiResponse = {
            ...formData,
            organisation: undefined,
            organisations: undefined,
            displayName: undefined
        };

        onSave(payload);
    };

    // Validation rule for shops
    const validateShops = (value: string[]) => {
        if (isSuperAdminOrOrganisationAdmin || formData.accessToAllStores) return '';
        if (!value || value.length === 0) {
            return t('required');
        }
        return '';
    };

    // Validation rule for organisations
    const validateOrganisations = (value: string[]) => {
        if (isSuperAdmin) return '';
        if (!value || value.length === 0) {
            return t('required');
        }
        return '';
    };

    const removeShop = (shopToRemove: string) => {
        const updatedShops = userSelectedShopIds.filter((shop) => shop !== shopToRemove);
        setUserSelectedShopIds(updatedShops);
        handleFieldChange('shopIds', updatedShops.length > 0 ? updatedShops : null);
    };

    const removeOrganisation = (orgToRemove: string) => {
        const updatedOrgs = userSelectedOrganisations.filter((org) => org !== orgToRemove);
        setUserSelectedOrganisations(updatedOrgs);
        handleFieldChange('organisationIds', updatedOrgs.length > 0 ? updatedOrgs : null);
    };

    // Role options
    const roleOptions = [
        { value: Role.SuperAdmin, label: 'SuperAdmin' },
        { value: Role.OrganisationAdmin, label: 'OrganisationAdmin' },
        { value: Role.UnitAdmin, label: 'UnitAdmin' },
        { value: Role.StoreAdmin, label: 'StoreAdmin' },
        { value: Role.User, label: 'User' }
    ];

    const isSelfEditing = !!item.id && !!user?.email && item.eMail?.toLowerCase() === user.email.toLowerCase();

    return (
        <Box className="user-editor editor-container">
            {/* Header Actions */}
            <Box className="editor-sidebar-actions">
                <Button variant="text" startIcon={<Save />} onClick={handleAskSave} disabled={!formValid}>
                    {t('save')}
                </Button>
                <Button variant="text" startIcon={<DoNotDisturbAlt />} onClick={onCancel}>
                    {t('cancel')}
                </Button>
                {formData.id && !isSelfEditing && (
                    <Button
                        variant="text"
                        color="error"
                        startIcon={<Delete />}
                        onClick={() => onDelete(formData)}
                        className="delete"
                    >
                        {t('delete')}
                    </Button>
                )}
            </Box>

            {/* Content */}
            <Box className="editor-sidebar-content component-editor-content">
                <Typography variant="h4" className="editor-section-header">
                    <Box component="span" className="material-icons">
                        person
                    </Box>
                    {t('user')}
                </Typography>

                <Box className="user-editor editor-form-container">
                    <FormContainer formRef={formRef} value={formValid} onChange={setFormValid}>
                        <div className="form-row">
                            <div className="form-col form-col-12">
                                <FormTextField
                                    name="givenName"
                                    label={t('firstName')}
                                    value={formData.givenName || ''}
                                    onChange={(e) => handleFieldChange('givenName', e.target.value)}
                                    required
                                    fullWidth
                                    FormHelperTextProps={{ sx: { minHeight: '20px' } }}
                                    rules={[required(t), validateUserName(t)]}
                                />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-col form-col-12">
                                <FormTextField
                                    name="surname"
                                    label={t('lastName')}
                                    value={formData.surname || ''}
                                    onChange={(e) => handleFieldChange('surname', e.target.value)}
                                    required
                                    fullWidth
                                    FormHelperTextProps={{ sx: { minHeight: '20px' } }}
                                    rules={[required(t), validateUserName(t)]}
                                />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-col form-col-12">
                                <FormTextField
                                    name="eMail"
                                    label={t('mailAddress')}
                                    type="email"
                                    value={formData.eMail}
                                    onChange={(e) => handleFieldChange('eMail', e.target.value)}
                                    required
                                    fullWidth
                                    FormHelperTextProps={{ sx: { minHeight: '20px' } }}
                                    rules={[required(t), validEmail(t)]}
                                    disabled={formData.id != null}
                                />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-col form-col-12">
                                <WcnCheckbox
                                    checked={formData.accessToAllStores ?? true}
                                    onChange={(e) => handleFieldChange('accessToAllStores', e.target.checked)}
                                    disabled={isSuperAdmin || isOrgAdmin || isSelfEditing}
                                    label={t('accessToAllShops')}
                                />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-col form-col-12">
                                <FormMultiSelect
                                    name="shops"
                                    label={t('selectShops')}
                                    value={userSelectedShopIds}
                                    items={userShopIds}
                                    onChange={handleMultipleShopChange}
                                    placeholder={t(
                                        formData.accessToAllStores || isSuperAdminOrOrganisationAdmin
                                            ? 'allShops'
                                            : 'selectShops'
                                    )}
                                    selectDisplayLabels={{
                                        placeholderLabel: t(
                                            formData.accessToAllStores || isSuperAdminOrOrganisationAdmin
                                                ? 'allShops'
                                                : 'selectShops'
                                        ),
                                        countLabel: t('selectedWithValue')
                                    }}
                                    rules={[validateShops]}
                                    itemText="name"
                                    itemValue="id"
                                    searchable
                                    disabled={isSuperAdminOrOrganisationAdmin || isSelfEditing}
                                />

                                <Box className="email-chips">
                                    {userShopIds
                                        .filter((shop) => userSelectedShopIds.includes(shop.id))
                                        .map((shop) => (
                                            <Chip
                                                key={shop.id}
                                                label={shop.name}
                                                onDelete={() => removeShop(shop.id)}
                                                className="email-chip"
                                                disabled={isSuperAdminOrOrganisationAdmin || isSelfEditing}
                                            />
                                        ))}
                                </Box>
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-col form-col-12">
                                <FormSelect
                                    name="role"
                                    label={t('role')}
                                    value={formData.role || Role.User}
                                    onChange={(value) => handleFieldChange('role', value)}
                                    items={roleOptions.filter((role) => hasRole(role.value))}
                                    itemText="label"
                                    itemValue="value"
                                    rules={[required(t)]}
                                    sx={{ width: 560 }}
                                    disabled={isSelfEditing}
                                />
                            </div>
                        </div>

                        {showOrganisationMultiSelect && (
                            <div className="form-row">
                                <div className="form-col form-col-12">
                                    <FormMultiSelect
                                        name="organisations"
                                        label={t('organisations')}
                                        value={userSelectedOrganisations}
                                        items={organisations}
                                        onChange={handleMultipleOrganisationChange}
                                        selectDisplayLabels={{
                                            placeholderLabel: t(
                                                isSuperAdmin ? 'allOrganisations' : 'selectOrganisations'
                                            ),
                                            countLabel: t('selectedWithValue')
                                        }}
                                        rules={[validateOrganisations]}
                                        itemText="name"
                                        itemValue="id"
                                        searchable
                                        disabled={formData.role !== Role.OrganisationAdmin || isSelfEditing}
                                    />
                                    <Box className="email-chips">
                                        {organisations
                                            .filter((org) => userSelectedOrganisations.includes(org.id))
                                            .map((org) => (
                                                <Chip
                                                    key={org.id}
                                                    label={org.name}
                                                    onDelete={() => removeOrganisation(org.id)}
                                                    className="email-chip"
                                                    disabled={formData.role !== Role.OrganisationAdmin || isSelfEditing}
                                                />
                                            ))}
                                    </Box>
                                </div>
                            </div>
                        )}

                        {formData.id && (
                            <div className="form-row">
                                <div className="form-col form-col-12">
                                    <WcnCheckbox
                                        checked={formData.accountEnabled ?? true}
                                        onChange={(e) => handleFieldChange('accountEnabled', e.target.checked)}
                                        color="primary"
                                        label={t('accountEnabled') || 'Account Enabled'}
                                        disabled={isSelfEditing}
                                    />
                                </div>
                            </div>
                        )}
                    </FormContainer>
                </Box>
            </Box>

            <ConfirmationDialogRequireInput
                dialogRef={confirmationDialogRef}
                heading={t('addOrganisationsToUserHeader')}
                message={t('confirmOrganisationsForUser', { user: formData.eMail || '' })}
                cancelText={t('cancel')}
                onConfirm={handleSave}
                expectedInputTexts={organisations
                    ?.filter(
                        (org) =>
                            userSelectedOrganisations.includes(org.id) && !existingUserOrganisations.includes(org.id)
                    )
                    .map((org) => org.name)}
            />
        </Box>
    );
};

export default UserEditor;
